import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Pagination } from '@/components/ui/Pagination';
import { Button } from '@/components/ui/Button';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { Table } from '@/components/ui/Table';
import { Badge, PriorityBadge } from '@/components/ui/Badge';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { useToast } from '@/components/ui/useToast';
import { useConfirm } from '@/components/ui/useConfirm';
import { usePrompt } from '@/components/ui/usePrompt';
import { useDocViewer } from '@/components/ui/useDocViewer';
import { FileText, MessageSquareQuote, Search, ShoppingCart } from '@/components/icons';
import { cancelEnquiry, enquiriesExportPath, generateOrder, listEnquiries, type Enquiry } from '@/api/enquiry';
import { downloadExcel } from '@/lib/downloadExcel';
import { Download } from '@/components/icons';
import { formatDate, formatINRSymbol } from '@/lib/format';
import { apiError } from '@/lib/apiError';
import { EnquiryFormModal } from './EnquiryFormModal';
import { useCart } from '@/cart/useCart';
import { orderRef } from '@/lib/orderRef';

const STAGE_TONE: Record<string, 'muted' | 'info' | 'warning' | 'success' | 'danger' | 'accent'> = {
  new: 'info',
  reviewed: 'accent',
  'do-created': 'accent',
  'credit-pending': 'warning',
  'credit-approved': 'success',
  'credit-rejected': 'danger',
  'pick-pending': 'accent',
  'pick-done': 'accent',
  'packing-done': 'accent',
  'grn-out': 'success',
  invoiced: 'success',
  'gate-out': 'success',
  fulfilled: 'success',
  cancelled: 'muted',
};

const CREDIT_TONE: Record<string, 'success' | 'warning' | 'danger'> = {
  GREEN: 'success',
  AMBER: 'warning',
  RED: 'danger',
};

export function EnquiryPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirmDlg = useConfirm();
  const ask = usePrompt();
  const docViewer = useDocViewer();
  const cart = useCart();
  const [searchParams, setSearchParams] = useSearchParams();
  const presetItem = searchParams.get('item');
  const presetCustomerType = searchParams.get('customerType');
  const fromCart = searchParams.get('fromCart') === '1';

  const [tab, setTab] = useState<'enquiry' | 'order'>('enquiry');
  const [partyType, setPartyType] = useState<'' | 'Lead' | 'Customer'>('');
  const [stage, setStage] = useState('');
  const [priority, setPriority] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Enquiry | null>(null);
  const [presetItemId, setPresetItemId] = useState<number | null>(null);
  const [presetCustomerTypeState, setPresetCustomerTypeState] = useState<string | null>(null);
  const [seedFromCart, setSeedFromCart] = useState(false);

  // If /sales/enq?item=N → auto-open modal with that item.
  // If /sales/enq?fromCart=1 → auto-open with every cart line pre-seeded.
  useEffect(() => {
    if (presetItem) {
      setPresetItemId(Number(presetItem));
      setPresetCustomerTypeState(presetCustomerType);
      setModalOpen(true);
      setSearchParams({}, { replace: true });
    } else if (fromCart) {
      setSeedFromCart(true);
      setPresetCustomerTypeState(presetCustomerType);
      setEditing(null);
      setPresetItemId(null);
      setModalOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [presetItem, presetCustomerType, fromCart, setSearchParams]);

  const filter = useMemo(
    () => ({
      stage: stage || undefined,
      flowType: tab,
      partyType: partyType || undefined,
      from: fromDate || undefined,
      to: toDate || undefined,
      priority: priority || undefined,
      search: search || undefined,
      page,
      pageSize,
    }),
    [tab, partyType, stage, priority, fromDate, toDate, search, page, pageSize],
  );

  const query = useQuery({
    queryKey: ['enquiries', filter],
    queryFn: () => listEnquiries(filter),
    placeholderData: keepPreviousData,
  });

  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => cancelEnquiry(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['enquiries'] });
      toast.push('Enquiry cancelled', 'info');
    },
    onError: (e: unknown) =>
      toast.push(apiError(e, 'Cancel failed'), 'error'),
  });

  const generateOrderMutation = useMutation({
    mutationFn: (id: number) => generateOrder(id),
    onSuccess: (order) => {
      qc.invalidateQueries({ queryKey: ['enquiries'] });
      toast.pushStep(`Order ${order.enqNo} generated`, {
        body: 'Next step — promote to a Dispatch Order.',
        href: '/sales/dispatch-order',
        nextLabel: 'Open Dispatch Orders',
        tag: `order-generated-${order.id}`,
      });
      // Nudge the sales rep straight to the Orders tab so they see the result.
      setTab('order');
      setPage(1);
    },
    onError: (e: unknown) =>
      toast.push(apiError(e, 'Could not generate order'), 'error'),
  });

  async function onCancel(e: Enquiry) {
    const reason = await ask({ title: `Cancel ${e.enqNo}?`, label: 'Reason', multiline: true, tone: 'danger', confirmLabel: 'Cancel enquiry' });
    if (!reason?.trim()) return;
    cancelMutation.mutate({ id: e.id, reason });
  }

  async function onGenerateOrder(e: Enquiry) {
    if (!await confirmDlg(`Convert enquiry ${e.enqNo} into a firm order? This cannot be undone (use Cancel if needed).`)) return;
    generateOrderMutation.mutate(e.id);
  }

  const data = query.data?.data ?? [];
  const total = query.data?.total ?? 0;

  return (
    <div>
      <SectionHeader
        title="Enquiry Capture"      />

      <div className="mb-2 flex items-center gap-2">
        <div className="inline-flex gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] p-1">
          <button
            type="button"
            onClick={() => { setTab('enquiry'); setPage(1); }}
            className={
              'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-bold transition ' +
              (tab === 'enquiry'
                ? 'bg-accent text-white shadow-sm'
                : 'text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-[var(--text-primary)]')
            }
            title="Non-committal quote requests"
          >
            <MessageSquareQuote size={13} strokeWidth={2.25} />
            Enquiries
          </button>
          <button
            type="button"
            onClick={() => { setTab('order'); setPage(1); }}
            className={
              'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-bold transition ' +
              (tab === 'order'
                ? 'bg-accent text-white shadow-sm'
                : 'text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-[var(--text-primary)]')
            }
            title="Firm orders — customer already committed"
          >
            <ShoppingCart size={13} strokeWidth={2.25} />
            Orders
          </button>
        </div>
        <div className="ml-auto pr-2 text-[11px] text-[var(--text-muted)]">
          Showing {tab === 'enquiry' ? 'enquiries' : 'firm orders'} only
        </div>
      </div>

      <FilterBar>
        {/* Mobile grid target (3 rows, dates paired):
              Row 1: Search(cols 1-2) · Party Type(col 3)
              Row 2: Stage · Priority · Export
              Row 3: From · To · New Enquiry
            Achieved via CSS `order` on mobile (Export bumped ahead of the
            dates so From/To sit together on the last row). Desktop resets
            via sm:order-none and keeps the flex-wrap + ml-auto pinning. */}
        <FilterField label="Search" className="col-span-2 sm:col-auto">
          <div className="relative w-full">
            <Search
              size={11}
              className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5"
              strokeWidth={2.25}
            />
            <Input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search"
              className="!pl-5 sm:!pl-8 sm:min-w-[260px]"
              aria-label="Search enquiries"
            />
          </div>
        </FilterField>
        <FilterField label="Party Type">
          <Select
            value={partyType}
            onChange={(e) => { setPartyType(e.target.value as '' | 'Lead' | 'Customer'); setPage(1); }}
          >
            <option value="">All parties</option>
            <option value="Lead">Leads</option>
            <option value="Customer">Customers</option>
          </Select>
        </FilterField>
        <FilterField label="Stage">
          <Select value={stage} onChange={(e) => { setStage(e.target.value); setPage(1); }}>
            <option value="">All</option>
            <option value="new">New</option>
            <option value="reviewed">Reviewed</option>
            <option value="do-created">DO Created</option>
            <option value="fulfilled">Fulfilled</option>
            <option value="cancelled">Cancelled</option>
          </Select>
        </FilterField>
        <FilterField label="Priority">
          <Select value={priority} onChange={(e) => { setPriority(e.target.value); setPage(1); }}>
            <option value="">All</option>
            <option>Low</option>
            <option>Normal</option>
            <option>High</option>
            <option>Urgent</option>
          </Select>
        </FilterField>
        <FilterField label="From date" className="order-2 sm:order-none">
          <Input
            type="date"
            value={fromDate}
            max={toDate || undefined}
            onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
            className="!px-1 sm:!px-2.5"
          />
        </FilterField>
        <FilterField label="To date" className="order-2 sm:order-none">
          <Input
            type="date"
            value={toDate}
            min={fromDate || undefined}
            onChange={(e) => { setToDate(e.target.value); setPage(1); }}
            className="!px-1 sm:!px-2.5"
          />
        </FilterField>
        <button
          type="button"
          onClick={() => downloadExcel(enquiriesExportPath(filter), 'GRHAMOY_Enquiries.xlsx')}
          title="Export current filtered list to Excel"
          className="order-1 inline-flex h-7 items-center justify-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-success hover:text-success sm:order-none sm:ml-auto sm:h-8 sm:self-end sm:gap-1.5 sm:px-3 sm:text-xs"
        >
          <Download size={12} /> Export
        </button>
        <button
          type="button"
          onClick={() => {
            setEditing(null);
            setPresetItemId(null);
            setModalOpen(true);
          }}
          className="order-3 inline-flex h-7 items-center justify-center gap-1 rounded-lg bg-accent px-2 text-[11px] font-bold text-white shadow-sm transition hover:brightness-110 sm:order-none sm:h-8 sm:self-end sm:gap-1.5 sm:px-3 sm:text-xs"
        >
          ＋ New Enquiry
        </button>
      </FilterBar>

      <Table<Enquiry>
        storageKey="enquiry"
        title="Enquiries"
        count={`Showing ${data.length} of ${total}`}
        rows={data}
        rowKey={(r) => r.id}
        loading={query.isFetching && data.length === 0}
        columns={[
          {
            key: 'enqNo',
            header: 'ENQ #',
            minWidth: '130px',
            cell: (r) => (
              <div className="flex items-center gap-1.5">
                <span className="font-extrabold text-accent" title={r.enqNo}>{orderRef(r)}</span>
                {r.flowType === 'order' && (
                  <span
                    className="rounded-md bg-accent/15 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wide text-accent"
                    title="Firm order — appears in Order Review"
                  >
                    Order
                  </span>
                )}
              </div>
            ),
          },
          { key: 'date', header: 'Date', minWidth: '105px', cell: (r) => formatDate(r.enqDate) },
          {
            key: 'party',
            header: 'Customer',
            minWidth: '180px',
            cell: (r) => (
              <div className="flex flex-col">
                <span className="font-bold">{r.party.name}</span>
                {r.party.customerType && (
                  <span className="text-[10px] text-[var(--text-muted)]">{r.party.customerType}</span>
                )}
              </div>
            ),
          },
          { key: 'salesRep', header: 'Sales Rep', minWidth: '110px', cell: (r) => r.salesRep },
          { key: 'items', header: 'Items', minWidth: '70px', align: 'right', cell: (r) => r.items.length },
          {
            key: 'value',
            header: 'Est. Value',
            minWidth: '120px',
            align: 'right',
            cell: (r) => formatINRSymbol(r.estOrderValue),
          },
          { key: 'priority', header: 'Priority', minWidth: '95px', cell: (r) => <PriorityBadge priority={r.priority} /> },
          {
            key: 'credit',
            header: 'Credit',
            minWidth: '90px',
            cell: (r) =>
              r.creditStatus ? (
                <Badge tone={CREDIT_TONE[r.creditStatus]}>{r.creditStatus}</Badge>
              ) : '—',
          },
          {
            key: 'mrn',
            header: 'MRN',
            minWidth: '110px',
            cell: (r) =>
              r.hasMrn ? (
                <span className="rounded-full bg-purple-400/15 px-2 py-0.5 text-[10px] font-bold text-purple-300">
                  ↳ Auto-raised
                </span>
              ) : '—',
          },
          {
            key: 'quotation',
            header: 'Doc',
            minWidth: '145px',
            cell: (r) => {
              const isOrder = r.flowType === 'order';
              const docType = isOrder ? 'sales-order' : 'quotation';
              return (
                <div className="flex flex-wrap gap-1.5">
                  <Button
                    variant="glass"
                    size="sm"
                    onClick={() => docViewer.open(docType, r.id, r.enqNo)}
                    title={isOrder ? 'View / print Sales Order PDF' : 'View / print quotation'}
                  >
                    <FileText size={14} />
                  </Button>
                  {isOrder && (
                    <span
                      className="inline-flex items-center rounded-md bg-accent/15 px-2 py-1 text-[10px] font-black text-accent"
                      title="Firm order — printed as Sales Order"
                    >
                      Sales Order
                    </span>
                  )}
                </div>
              );
            },
          },
          {
            key: 'stage',
            header: 'Stage',
            minWidth: '110px',
            cell: (r) => <Badge tone={STAGE_TONE[r.stage] ?? 'muted'}>{r.stage}</Badge>,
          },
          {
            key: 'actions',
            header: '',
            minWidth: '160px',
            cell: (r) => {
              const canGenerate =
                r.stage === 'new' &&
                r.flowType === 'enquiry' &&
                r.party?.customerType !== 'Lead';
              const isLead = r.party?.customerType === 'Lead';
              return (
                <div className="flex flex-wrap gap-1.5">
                  <Button
                    variant="glass"
                    size="sm"
                    onClick={() => { setEditing(r); setPresetItemId(null); setModalOpen(true); }}
                  >
                    View
                  </Button>
                  {r.stage === 'new' && r.flowType === 'enquiry' && (
                    <Button
                      variant="warning"
                      size="sm"
                      onClick={() => onGenerateOrder(r)}
                      disabled={!canGenerate || generateOrderMutation.isPending}
                      title={
                        isLead
                          ? 'Convert this Lead to a Customer in Party Master first'
                          : 'Customer accepted — convert this enquiry into a firm order'
                      }
                    >
                      Generate Order
                    </Button>
                  )}
                  {r.stage === 'new' && (
                    <Button variant="danger" size="sm" onClick={() => onCancel(r)}>Cancel</Button>
                  )}
                </div>
              );
            },
          },
        ]}
        footer={<Pagination page={page} pageSize={pageSize} total={total} setPage={setPage} setPageSize={setPageSize} />}
      />

      <EnquiryFormModal
        open={modalOpen}
        editing={editing}
        presetItemId={presetItemId}
        presetCustomerType={presetCustomerTypeState}
        presetCartItems={seedFromCart ? (cart.cart?.items.map((it: { itemId: number; qty: number }) => ({
          itemId: it.itemId,
          qty: it.qty,
        })) ?? []) : undefined}
        onClose={() => { setModalOpen(false); setSeedFromCart(false); }}
        onSaved={async () => {
          if (seedFromCart) { await cart.clear(); }
          setSeedFromCart(false);
        }}
      />
    </div>
  );
}
